import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import {
  AuthState,
  User,
  CompanyTokenData,
  DynamicCompanyTokens
} from '../model/auth.models';

const DEFAULT_STATE: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  currentCompany: null,
  isCustomerLogin: false,
  error: null,
  lastActivity: null,
  sessionExpiry: null
};

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  // ─── Primary auth state ──────────────────────────────────────────────────

  private authStateSubject = new BehaviorSubject<AuthState>({ ...DEFAULT_STATE });
  private companyTokensSubject = new BehaviorSubject<DynamicCompanyTokens>({});

  // ─── Observables ─────────────────────────────────────────────────────────

  readonly authState$: Observable<AuthState> = this.authStateSubject.asObservable();

  readonly user$: Observable<User | null> = this.authStateSubject.pipe(
    map(s => s.user),
    distinctUntilChanged()
  );

  readonly isAuthenticated$: Observable<boolean> = this.authStateSubject.pipe(
    map(s => s.isAuthenticated),
    distinctUntilChanged()
  );

  readonly currentCompany$: Observable<string | null> = this.authStateSubject.pipe(
    map(s => s.currentCompany),
    distinctUntilChanged()
  );

  readonly isLoading$: Observable<boolean> = this.authStateSubject.pipe(
    map(s => s.isLoading),
    distinctUntilChanged()
  );

  readonly companyTokens$: Observable<DynamicCompanyTokens> =
    this.companyTokensSubject.asObservable();

  // ─── Synchronous getters (safe to use in interceptors) ───────────────────

  get user(): User | null {
    return this.authStateSubject.value.user;
  }

  get isAuthenticated(): boolean {
    return this.authStateSubject.value.isAuthenticated;
  }

  get isLoading(): boolean {
    return this.authStateSubject.value.isLoading;
  }

  get currentCompany(): string | null {
    return this.authStateSubject.value.currentCompany;
  }

  get token(): string | null {
    return this.authStateSubject.value.token;
  }

  get permissions(): string[] {
    return this.authStateSubject.value.user?.permissions || [];
  }

  // ─── State mutations ──────────────────────────────────────────────────────

  setAuthState(updates: Partial<AuthState>): void {
    const newState = { ...this.authStateSubject.value, ...updates };
    this.authStateSubject.next(newState);
  }

  setLoading(loading: boolean): void {
    this.setAuthState({ isLoading: loading });
  }

  setError(error: string | null): void {
    this.setAuthState({ error });
  }

  reset(): void {
    this.authStateSubject.next({ ...DEFAULT_STATE });
    this.companyTokensSubject.next({});
  }

  // ─── Company token management ─────────────────────────────────────────────

  updateCompanyToken(companyName: string, tokenData: CompanyTokenData): void {
    const current = this.companyTokensSubject.value;
    this.companyTokensSubject.next({ ...current, [companyName]: tokenData });
  }

  getCompanyTokenData(companyName: string): CompanyTokenData | undefined {
    return this.companyTokensSubject.value[companyName];
  }

  getCompanyTokens(): DynamicCompanyTokens {
    return this.companyTokensSubject.value;
  }

  get availableCompanies(): string[] {
    return Object.keys(this.companyTokensSubject.value);
  }

  get hasMultipleCompanies(): boolean {
    return this.availableCompanies.length > 1;
  }

  setCompanyTokens(tokens: DynamicCompanyTokens): void {
    this.companyTokensSubject.next({ ...tokens });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  getUserName(): string {
    return this.user?.userName || '';
  }

  hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  /**
   * Hydrate auth state from localStorage on page refresh.
   * Call this once from AppComponent.ngOnInit() after integrity check passes.
   */
  hydrateFromStorage(): void {
    try {
      const company = localStorage.getItem('currentCompany');
      if (!company) return;

      const isLoggedIn = localStorage.getItem('isLoggedIn');
      const isCustomerLogin = localStorage.getItem('isCustomerLogin');

      if (isLoggedIn !== 'true' || isCustomerLogin !== 'true') return;

      const userRaw = localStorage.getItem(`tibss.${company}-userDetails`);
      const user: User | null = userRaw ? JSON.parse(userRaw) : null;
      if (!user) return;

      this.setAuthState({
        user,
        token: null,
        isAuthenticated: false,
        currentCompany: company,
        isCustomerLogin: true,
        lastActivity: new Date()
      });

      const appsRaw = localStorage.getItem('Applications');
      const apps = appsRaw ? JSON.parse(appsRaw) : [];
      const companies: string[] = apps.map((a: any) => a.name).filter(Boolean);

      if (!companies.includes(company)) companies.push(company);

      for (const co of companies) {
        const coUserRaw = localStorage.getItem(`tibss.${co}-userDetails`);
        const coUser: User | null = coUserRaw ? JSON.parse(coUserRaw) : null;
        if (!coUser) continue;

        this.updateCompanyToken(co, {
          userId: coUser?.userID
        });
      }
    } catch (err) {
      console.error('[AuthStateService] Hydration failed:', err);
    }
  }
}
